import { useRef } from 'react'

import classnames from 'classnames'
import { Navbar } from 'react-bootstrap'
import Nav from 'react-bootstrap/Nav'
import { useTranslation } from 'react-i18next'
import {
  provideComponent,
  ChildListTag,
  LinkTag,
  isInPlaceEditingActive,
  Obj,
  currentPage,
  useDataItem,
} from 'scrivito'

import { DXPSidebarLeftWidget } from './DXPSidebarLeftWidgetClass'
import { Icon } from '@/Atomic/Atoms/Icon'
import { StringText } from '@/Atomic/Atoms/StringText'
import { useSidebarContext } from '@/Components/Sidebar/SidebarContext'
import { PagePermaLink } from '@/const'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { getOverviewPageLink } from '@/utils/dataClassMap'
import { useFeatureFlagAccess } from '@/utils/featureFlagConfig'
import { objTitle } from '@/utils/objTitle'

import './DXPSidebarLeftWidget.scss'

provideComponent(DXPSidebarLeftWidget, ({ widget }) => {
  const { t } = useTranslation()
  const page = widget.obj()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const { isPageEnabled } = useFeatureFlagAccess()
  const { isOpen, closeSidebar } = useSidebarContext()

  useOnClickOutside({
    elementRef: sidebarRef,
    onClick: () => {
      if (isOpen) {
        closeSidebar()
      }
    },
  })

  const hideChild = (child: Obj) => {
    const permalink = child.permalink()

    if (permalink === PagePermaLink.SEARCH_RESULTS) {
      return true
    }
    // hide myContacts for now
    if (permalink === PagePermaLink.MY_CONTACTS) {
      return true
    }
    return !isPageEnabled(permalink)
  }

  const dashboardPage = Obj.getByPermalink(PagePermaLink.DASHBOARD)

  const currentPageObj = currentPage()
  const activePage = currentPageObj?.permalink()
  // When viewing an entity detail page, the page is a separate Obj reached via
  // a data param rather than a child of the list page, so an exact permalink
  // match isn't enough. Map the current data item's class to its overview page
  // (e.g. Company -> "companies") to keep the list item highlighted, and fall
  // back to the page-tree ancestors for genuinely nested pages.
  const currentDataItem = useDataItem()
  const overviewPermalink = currentDataItem
    ? getOverviewPageLink(currentDataItem.dataClassName() ?? '')
    : ''
  const ancestorPermalinks =
    currentPageObj
      ?.ancestors()
      .map((ancestor) => ancestor?.permalink())
      .filter((permalink): permalink is string => !!permalink) ?? []
  const isPermalinkActive = (permalink: string | null) =>
    !!permalink &&
    (activePage === permalink ||
      (!!overviewPermalink && overviewPermalink === permalink) ||
      ancestorPermalinks.includes(permalink))

  // Dashboard has no detail/descendant pages, so it should only highlight on an
  // exact match (it would otherwise always match as an ancestor of every page).
  const isDashboardActive =
    !!dashboardPage && activePage === dashboardPage.permalink()

  return (
    <Navbar className="dxp-sidebar-left-widget">
      {isInPlaceEditingActive() ? <div className="mb-3" /> : null}
      <Navbar.Collapse id="nav-sidebar">
        {dashboardPage && isPageEnabled(PagePermaLink.DASHBOARD) && (
          <Nav.Link
            className={classnames('dxp-sidebar-buttonbar', {
              active: isDashboardActive,
            })}
            as={LinkTag}
            eventKey={`VerticalNavigationWidget-${widget.id()}-${page.id()}-${dashboardPage.id()}`}
            key={`VerticalNavigationWidget-${widget.id()}-${page.id()}-${dashboardPage.id()}`}
            to={dashboardPage}
            onClick={closeSidebar}
          >
            <Icon icon="dashboard" color="default" />
            <StringText
              wrapperClassName="dxp-sidebar-label"
              value={t('pages.dashboard.title')}
            />
          </Nav.Link>
        )}

        <ChildListTag
          tag="span"
          parent={page}
          renderChild={(child) => {
            const isActive = isPermalinkActive(child.permalink())
            return hideChild(child) ? (
              <></>
            ) : (
              <Nav.Link
                className={classnames('dxp-sidebar-buttonbar', {
                  active: isActive,
                })}
                as={LinkTag}
                eventKey={`VerticalNavigationWidget-${widget.id()}-${page.id()}-${child.id()}`}
                key={`VerticalNavigationWidget-${widget.id()}-${page.id()}-${child.id()}`}
                to={child}
                onClick={closeSidebar}
              >
                <Icon
                  icon={String(child.get('linkIcon') || '')}
                  color="default"
                />
                <StringText
                  wrapperClassName="dxp-sidebar-label"
                  value={t(`sidebar.navigation.item.${objTitle(child)}`)}
                />
              </Nav.Link>
            )
          }}
        />
      </Navbar.Collapse>
    </Navbar>
  )
})
